/**
 * TeamManagement — admin-only section to invite/promote/demote fellow teachers.
 *
 * Backend contract (see /app/backend/routes/invites.py):
 *   - GET    /api/admin/teachers                    → list teachers + admins
 *   - POST   /api/admin/teachers/promote            → { email }
 *   - PATCH  /api/admin/teachers/{user_id}          → { role: "student" | "teacher" }
 *   - POST   /api/teacher/invites                   → { email, name?, message?, role: "teacher" }
 *
 * Access: only visible when the current user is `role: admin`. The Teacher
 * Dashboard hides the tab for regular teachers.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  UserPlus,
  Envelope,
  ChalkboardTeacher,
  ShieldCheck,
  ArrowCircleDown,
  Spinner,
  Crown,
  CheckCircle,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamManagement({ currentUserId }) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoting, setPromoting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { email, temp_password, login_url }

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/teachers`, { withCredentials: true });
      setTeachers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Laden der Lehrer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePromote = async (e) => {
    e.preventDefault();
    if (!promoteEmail.trim()) return;
    setPromoting(true);
    try {
      const res = await axios.post(
        `${API}/admin/teachers/promote`,
        { email: promoteEmail.trim() },
        { withCredentials: true }
      );
      if (res.data.already) {
        toast.info('Dieser Nutzer ist bereits Lehrer.');
      } else {
        toast.success(`${res.data.user?.name || res.data.user?.email} ist jetzt Lehrer.`);
      }
      setPromoteEmail('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Promotion fehlgeschlagen');
    } finally {
      setPromoting(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const res = await axios.post(
        `${API}/teacher/invites`,
        {
          email: inviteEmail.trim(),
          name: inviteName.trim() || null,
          message: inviteMessage.trim() || null,
          role: 'teacher',
        },
        { withCredentials: true }
      );
      toast.success(res.data.email_sent
        ? 'Einladung gesendet — der neue Lehrer bekommt eine E-Mail mit Zugangsdaten.'
        : 'Konto erstellt (E-Mail-Versand fehlgeschlagen — Zugangsdaten unten kopieren).');
      // Backend returns { temp_password, login_url } for teacher invites so
      // the admin can share credentials manually if email failed.
      setInviteResult({
        email: inviteEmail.trim(),
        temp_password: res.data.temp_password || '',
        login_url: res.data.login_url || '',
        email_sent: res.data.email_sent,
      });
      setInviteEmail('');
      setInviteName('');
      setInviteMessage('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Einladung fehlgeschlagen');
    } finally {
      setInviting(false);
    }
  };

  const handleDemote = async (userId, name) => {
    if (!window.confirm(`${name} wirklich zum Schüler zurückstufen? Er/Sie verliert alle Lehrer-Rechte, das Konto bleibt aktiv.`)) return;
    try {
      await axios.patch(
        `${API}/admin/teachers/${userId}`,
        { role: 'student' },
        { withCredentials: true }
      );
      toast.success(`${name} wurde zum Schüler zurückgestuft.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rückstufung fehlgeschlagen');
    }
  };

  const copyText = async (text, label = 'Text') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert!`);
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-6" data-testid="team-management">
      <div>
        <h2 className="font-heading text-xl font-bold text-[#292524] flex items-center gap-2">
          <ChalkboardTeacher size={22} weight="duotone" className="text-[#AB1C24]" />
          Team
        </h2>
        <p className="text-sm text-[#78716C] mt-1">
          Verwalte deine Mit-Lehrer: lade neue Lehrer ein, befördere bestehende Nutzer oder stufe sie zurück.
          Jeder Lehrer kann Klassen anlegen, Schüler hinzufügen und kostenlose Einladungen versenden — genau wie du.
        </p>
      </div>

      {/* Two-column forms: invite + promote */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Invite new teacher */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Envelope size={18} weight="duotone" className="text-emerald-600" />
            <h3 className="font-semibold text-[#292524]">Neuen Lehrer per E-Mail einladen</h3>
          </div>
          <p className="text-xs text-[#78716C]">
            Der Empfänger bekommt einen persönlichen Link, um sich als Lehrer zu registrieren
            (inkl. lebenslangem Premium-Zugang).
          </p>
          <form onSubmit={handleInvite} className="space-y-3">
            <div>
              <Label htmlFor="team-invite-email" className="text-xs">E-Mail-Adresse *</Label>
              <Input
                id="team-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="neuer.lehrer@beispiel.de"
                required
                data-testid="team-invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="team-invite-name" className="text-xs">Name (optional)</Label>
              <Input
                id="team-invite-name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Max Mustermann"
                data-testid="team-invite-name-input"
              />
            </div>
            <div>
              <Label htmlFor="team-invite-msg" className="text-xs">Persönliche Nachricht (optional)</Label>
              <Input
                id="team-invite-msg"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Willkommen im Habla-Team!"
                data-testid="team-invite-msg-input"
              />
            </div>
            <Button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="team-invite-submit"
            >
              {inviting ? <Spinner size={16} className="animate-spin mr-2" /> : <UserPlus size={16} weight="fill" className="mr-2" />}
              Einladung senden
            </Button>
          </form>
          {inviteResult && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="team-invite-result">
              <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                <CheckCircle size={14} weight="fill" />
                {inviteResult.email_sent
                  ? 'Konto erstellt & E-Mail gesendet'
                  : 'Konto erstellt — E-Mail fehlgeschlagen, bitte Zugangsdaten manuell weitergeben'}
              </p>
              <div className="space-y-2 pt-1">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700/70 mb-1">E-Mail</p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={inviteResult.email}
                      className="flex-1 text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 font-mono"
                      onClick={(e) => e.target.select()}
                    />
                    <Button size="sm" variant="outline" onClick={() => copyText(inviteResult.email, 'E-Mail')}>
                      Kopieren
                    </Button>
                  </div>
                </div>
                {inviteResult.temp_password && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700/70 mb-1">Temporäres Passwort (nur einmal sichtbar)</p>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={inviteResult.temp_password}
                        className="flex-1 text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 font-mono font-bold text-[#AB1C24]"
                        onClick={(e) => e.target.select()}
                        data-testid="team-invite-temp-password"
                      />
                      <Button size="sm" variant="outline" onClick={() => copyText(inviteResult.temp_password, 'Passwort')} data-testid="team-invite-copy-pw">
                        Kopieren
                      </Button>
                    </div>
                    <p className="text-[10px] text-emerald-800/60 mt-1">
                      Der neue Lehrer wird beim ersten Login aufgefordert, ein eigenes Passwort zu wählen.
                    </p>
                  </div>
                )}
                {inviteResult.login_url && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-700/70 mb-1">Login-Link</p>
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={inviteResult.login_url}
                        data-testid="team-invite-login-url"
                        className="flex-1 text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 font-mono"
                        onClick={(e) => e.target.select()}
                      />
                      <Button size="sm" variant="outline" onClick={() => copyText(inviteResult.login_url, 'Link')}>
                        Kopieren
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Promote existing user */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-[#AB1C24]" />
            <h3 className="font-semibold text-[#292524]">Bestehenden Nutzer zum Lehrer befördern</h3>
          </div>
          <p className="text-xs text-[#78716C]">
            Nutze diese Option, wenn die Person bereits ein Habla-Konto hat.
            Sie behält all ihre Daten und bekommt sofort Lehrer-Rechte.
          </p>
          <form onSubmit={handlePromote} className="space-y-3">
            <div>
              <Label htmlFor="team-promote-email" className="text-xs">E-Mail des bestehenden Nutzers *</Label>
              <Input
                id="team-promote-email"
                type="email"
                value={promoteEmail}
                onChange={(e) => setPromoteEmail(e.target.value)}
                placeholder="nutzer@beispiel.de"
                required
                data-testid="team-promote-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={promoting || !promoteEmail.trim()}
              className="w-full bg-[#AB1C24] hover:bg-[#8A151C] text-white"
              data-testid="team-promote-submit"
            >
              {promoting ? <Spinner size={16} className="animate-spin mr-2" /> : <ShieldCheck size={16} weight="fill" className="mr-2" />}
              Befördern
            </Button>
          </form>
        </div>
      </div>

      {/* Current team list */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#292524]">Aktuelles Team ({teachers.length})</h3>
          {loading && <Spinner size={16} className="animate-spin text-[#78716C]" />}
        </div>
        {!loading && teachers.length === 0 && (
          <p className="text-sm text-[#78716C]">Noch keine Lehrer im Team.</p>
        )}
        <div className="space-y-2">
          {teachers.map(t => {
            const isAdmin = t.role === 'admin';
            const isSelf = t.user_id === currentUserId;
            return (
              <div
                key={t.user_id}
                className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${
                  isAdmin ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-200'
                }`}
                data-testid={`team-member-${t.user_id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {t.avatar ? (
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white border border-stone-300 flex items-center justify-center text-[#78716C] font-bold">
                      {(t.name || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#292524] truncate">{t.name || t.email}</p>
                      {isAdmin && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded-full">
                          <Crown size={10} weight="fill" /> Admin
                        </span>
                      )}
                      {!isAdmin && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#AB1C24] text-white px-2 py-0.5 rounded-full">
                          <ChalkboardTeacher size={10} weight="fill" /> Lehrer
                        </span>
                      )}
                      {isSelf && (
                        <span className="text-[10px] uppercase tracking-wider text-[#78716C]">(du)</span>
                      )}
                    </div>
                    <p className="text-xs text-[#78716C] truncate">{t.email}</p>
                  </div>
                </div>
                {!isAdmin && !isSelf && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDemote(t.user_id, t.name || t.email)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`team-demote-${t.user_id}`}
                  >
                    <ArrowCircleDown size={14} className="mr-1" />
                    Zurückstufen
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
